import Component from '@glimmer/component';
import { assert } from '@ember/debug';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { PhArrowDown, PhArrowUp } from 'ember-phosphor-icons';

export type SortFormat = 'prefix' | 'suffix';

export interface TableSortableHeaderSignature {
  Args: {
    tableSort: string;
    sortField: string;
    onSort: (sortField: string) => void;
    /**
     * Controls the format of the sort string passed to onSort:
     * - 'prefix' (default): Uses "-fieldName" for descending (Rails/JSON:API convention)
     * - 'suffix': Uses "fieldName:desc" for descending (OpenSearch convention)
     */
    sortFormat?: SortFormat;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLTableCellElement;
}

export default class TableSortableHeaderComponent extends Component<TableSortableHeaderSignature> {
  constructor(owner: Owner, args: TableSortableHeaderSignature['Args']) {
    super(owner, args);
    if (!args.sortField) {
      assert(
        `The component 'table-sortable-header' expects argument 'sortField' to be a valid string.`
      );
    }
  }

  get sortFormat(): SortFormat {
    return this.args.sortFormat ?? 'prefix';
  }

  get sortDescending() {
    if (!this.isActiveSort) {
      return false;
    }
    if (this.sortFormat === 'suffix') {
      return this.args.tableSort.endsWith(':desc');
    }
    return this.args.tableSort.startsWith('-');
  }

  get isActiveSort() {
    const tableSort = this.args.tableSort;
    if (!tableSort) {
      return false;
    }
    let currentSort: string;
    if (this.sortFormat === 'suffix') {
      // Remove :asc or :desc suffix
      currentSort = tableSort.replace(/:(?:asc|desc)$/, '');
    } else {
      // Remove leading - prefix
      currentSort = tableSort.startsWith('-') ? tableSort.slice(1) : tableSort;
    }

    return currentSort === this.args.sortField;
  }

  get ariaSort() {
    if (!this.isActiveSort) {
      return 'none';
    }
    return this.sortDescending ? 'descending' : 'ascending';
  }

  didSort = () => {
    let sort = this.args.sortField;

    if (this.isActiveSort) {
      const shouldSortDesc = !this.sortDescending;
      if (this.sortFormat === 'suffix') {
        sort = shouldSortDesc ? `${this.args.sortField}:desc` : `${this.args.sortField}:asc`;
      } else {
        sort = shouldSortDesc ? `-${this.args.sortField}` : this.args.sortField;
      }
    }

    this.args.onSort?.(sort);
  };

  <template>
    <th scope="col" aria-sort={{this.ariaSort}} data-table-head-cell ...attributes>
      <button
        type="button"
        {{on "click" this.didSort}}
        class="flex items-center justify-start h-full w-full"
      >
        {{yield}}
        {{#if this.isActiveSort}}
          {{#if this.sortDescending}}
            <PhArrowDown @size="14" class="ml-1" data-test-sort-asc />
          {{else}}
            <PhArrowUp @size="14" class="ml-1" data-test-sort-desc />
          {{/if}}
        {{/if}}
      </button>
    </th>
  </template>
}
